import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import EleicaoMesaTV from '../components/EleicaoMesaTV'; // PLENARIUM:ELEICAO_MESA
import LeituraTV from '../components/LeituraTV'; // PLENARIUM:LEITURAS
import { TribunaTV } from '../components/Tribuna'; // PLENARIUM:TRIBUNA
import { useCerimonialTV } from '../components/CerimonialTV'; // PLENARIUM:TV_CERIMONIAL
import { useConducaoTV, TelaFaseTV } from '../components/ConducaoTV'; // PLENARIUM:CONDUCAO
import { quorumDaCamara } from '../components/EstruturaSessao'; // PLENARIUM:QUORUM_CAMARA
import { MateriaTV } from '../components/MateriaTV'; // PLENARIUM:TV_SOBERANA
import AguardandoTV from '../components/AguardandoTV'; // PLENARIUM:TV_AGUARDANDO


// ── Nome exibido nas telas ──────────────────────────────────────────
// Usa o nome parlamentar quando houver. Documentos oficiais (ata, lista
// de presença, relatórios) continuam usando SEMPRE o nome civil.
const exib = (v) => (v && (v.nomeParlamentar || v.nome)) || '';
const exibCurto = (v) => {
  if (!v) return '';
  if (v.nomeParlamentar) return v.nomeParlamentar;
  const p = String(v.nome || '').trim().split(/\s+/);
  return p.length > 1 ? p[0] + ' ' + p[p.length - 1] : (p[0] || '');
};
// O socket envia o nome CIVIL do orador; aqui só descobrimos como exibi-lo.
const exibPorNome = (lista, nome) => {
  const achado = (lista || []).find(x => x.nome === nome);
  return (achado && achado.nomeParlamentar) || nome || '';
};

// ── Tratamento do parlamentar ───────────────────────────────────────
// "Presidente" e "Vice-Presidente" NAO variam com o sexo (decisão da casa).
// Sem sexo cadastrado, mantém "Vereador", que era o comportamento anterior.
const tratamento = (v) => {
  if (!v) return 'Vereador';
  if (v.cargo === 'PRESIDENTE') return 'Presidente';
  if (v.cargo === 'VICE_PRESIDENTE') return 'Vice-Presidente';
  return v.sexo === 'F' ? 'Vereadora' : 'Vereador';
};
const s = (base) => `clamp(${base * 0.6}px, ${(base / 1920) * 100}vw, ${base * 1.3}px)`;


function RelogioGrande({ s }) {
  const [agora, setAgora] = React.useState(new Date());
  React.useEffect(() => { const t = setInterval(() => setAgora(new Date()), 1000); return () => clearInterval(t); }, []);
  const h = agora.getHours().toString().padStart(2,'0');
  const m = agora.getMinutes().toString().padStart(2,'0');
  const sec = agora.getSeconds().toString().padStart(2,'0');
  const dataFmt = `${agora.getDate().toString().padStart(2,'0')}/${(agora.getMonth()+1).toString().padStart(2,'0')}/${agora.getFullYear()}`;
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: 'monospace', fontSize: s(84), fontWeight: 900, color: 'white', letterSpacing: s(5), lineHeight: 1 }}>
        {h}<span style={{ color: 'white' }}>:</span>{m}<span style={{ color: 'white' }}>:</span>{sec}
      </div>
      <div style={{ fontSize: s(19), color: 'rgba(255,255,255,0.5)', marginTop: s(10) }}>📅 {dataFmt}</div>
    </div>
  );
}

function Relogio() {
  const [hora, setHora] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setHora(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: 'monospace', fontSize: s(32), fontWeight: 900, color: 'white', letterSpacing: 2, lineHeight: 1 }}>
        {format(hora, 'HH:mm:ss')}
      </div>
      <div style={{ fontSize: s(13), color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
        {format(hora, "dd/MM/yyyy", { locale: ptBR })}
      </div>
    </div>
  );
}

// >>> PLENARIUM:TV_VOTO_ETIQUETA -- etiqueta de como cada vereador votou (icone + texto)
const ETIQUETA_VOTO = {
  SIM:        { fundo: 'rgba(34,197,94,0.16)',  borda: 'rgba(34,197,94,0.6)',  txt: '#dcfce7', ic: '#22c55e' },
  NAO:        { fundo: 'rgba(239,68,68,0.17)',  borda: 'rgba(239,68,68,0.6)',  txt: '#fee2e2', ic: '#ef4444' },
  ABSTENCAO:  { fundo: 'rgba(234,179,8,0.2)',   borda: 'rgba(234,179,8,0.75)', txt: '#fde68a', ic: '#facc15' },
  VOTOU:      { fundo: 'rgba(59,130,246,0.18)', borda: 'rgba(59,130,246,0.6)', txt: '#dbeafe', ic: '#3b82f6' },
  AUSENTE:    { fundo: 'rgba(148,163,184,0.12)', borda: 'rgba(148,163,184,0.4)', txt: '#cbd5e1', ic: '#94a3b8' },
  AGUARDANDO: { fundo: 'rgba(255,255,255,0.05)', borda: 'rgba(255,255,255,0.16)', txt: '#94a3b8', ic: '#94a3b8' }
};
function IconeVotoTV({ tipo, cor }) {
  const t = '2.3vh';
  if (tipo === 'AUSENTE') return (
    <svg width={t} height={t} viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="8" r="4" /><path d="M2.5 21v-.5A6.5 6.5 0 0 1 12 15" /><path d="M16 15l5 5M21 15l-5 5" />
    </svg>
  );
  if (tipo === 'AGUARDANDO') return (
    <svg width={t} height={t} viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.2 2" />
    </svg>
  );
  const traco = tipo === 'NAO' ? 'M8 8l8 8M16 8l-8 8' : tipo === 'ABSTENCAO' ? 'M7 12h10' : 'M6.8 12.6l3.4 3.4L17.4 8.6';
  return (
    <svg width={t} height={t} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="12" fill={cor} />
      <path d={traco} fill="none" stroke="#0b1220" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
// <<< PLENARIUM:TV_VOTO_ETIQUETA
const TIPO_LABEL = {
  PROJETO_LEI: 'Projeto de Lei', PROJETO_RESOLUCAO: 'Proj. Resolução',
  REQUERIMENTO: 'Requerimento', MOCAO: 'Moção', INDICACAO: 'Indicação', OUTROS: 'Outros',
  ATA_ANTERIOR: 'Ata da Sessão Anterior'
};

export default function PainelTV() {
  React.useEffect(() => { document.body.classList.add('pl-fonte-tv'); return () => document.body.classList.remove('pl-fonte-tv'); }, []); // PLENARIUM:FONTE_INTER -- TV fica com a DM Sans
  const { sessaoId, slug } = useParams();
  const [searchParams] = useSearchParams();
  const camaraIdParam = searchParams.get('camaraId');

  const [sessao, setSessao] = useState(null);
  const [pautaAtiva, setPautaAtiva] = useState(null);
  // Depois de encerrar uma votação a TV mostra o resultado por alguns
  // segundos e volta sozinha para a Ordem do Dia. Antes ela ficava presa
  // na tela de votação porque pautaAtiva nunca era limpa.
  const voltarOrdemRef = useRef(null);
  // PLENARIUM:TV_TEMPO_REAL -- controle das recargas (so vale a resposta mais nova) e do ultimo voto recebido ao vivo
  const seqCarregarRef = useRef(0);
  const ultimoVotoAoVivoRef = useRef(0);
  const sessaoAtualRef = useRef(null);
  const [vereadores, setVereadores] = useState([]);
  const [presencas, setPresencas] = useState([]);
  const [chamada2TV, setChamada2TV] = useState(null); // PLENARIUM:DUAS_CHAMADAS
  const chamada2Ref = useRef(null); chamada2Ref.current = chamada2TV; // PLENARIUM:AJUSTES_2110
  const [loginExpirado, setLoginExpirado] = useState(false); // PLENARIUM:AJUSTES_2110 -- o acesso desta TV venceu (login dura 8 horas)
  const [contagem, setContagem] = useState({ SIM: 0, NAO: 0, ABSTENCAO: 0 });
  const [votosMap, setVotosMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [cronometro, setCronometro] = useState(null);
  const [tempoLocal, setTempoLocal] = useState(0);
  // Guarda qual cronômetro já teve o encerramento anunciado. O zero pode
  // chegar por dois caminhos (contagem local e aviso do servidor); sem
  // isto, o som e a mensagem sairiam duas vezes.
  const anuncioRef = useRef(null);
  const cronIntervalRef = React.useRef(null);
  const [tempoEncerrado, setTempoEncerrado] = useState(false);
  const encerradoTimerRef = React.useRef(null);
  const [pautas, setPautas] = useState([]);
  const [camaraId, setCamaraId] = useState(camaraIdParam || null);
  const [camaraInfo, setCamaraInfo] = useState(null);
  useCerimonialTV({ sessao, loading, camaraInfo }); // PLENARIUM:TV_CERIMONIAL -- boas-vindas e agradecimento
  // PLENARIUM:QUORUM_CAMARA -- quorum definido pela camara (Estrutura da sessao)
  const [estruturaTV, setEstruturaTV] = useState(null);
  useEffect(() => { const t = localStorage.getItem('token'); axios.get(`/api/estrutura-sessao${camaraId ? `?camaraId=${camaraId}` : ''}`, { headers: t ? { Authorization: `Bearer ${t}` } : {} }).then(r => setEstruturaTV(r.data)).catch(() => {}); }, [camaraId]);
  // >>> PLENARIUM:CONDUCAO -- o que o administrador mandou mostrar
  const conducaoTV = useConducaoTV({ sessaoId: sessao ? sessao.id : null, sessao, camaraInfo });
  // PLENARIUM:TV_SOBERANA -- escolha manual do admin manda: some tribuna, leitura, cronometro e votacao
  // (abrir algo novo devolve a TV ao Automatico no servidor). "Ultimo resultado" continua mostrando o resultado.
  const tvManual = !!conducaoTV.modo && conducaoTV.modo !== 'AUTO';
  const esconderVotacao = tvManual && conducaoTV.modo !== 'RESULTADO';
  useEffect(() => { // "Ultimo resultado": mostra o resultado escolhido ate o administrador trocar
    if (conducaoTV.modo !== 'RESULTADO') return;
    const p = pautas.find(x => x.id === (conducaoTV.dados && conducaoTV.dados.pautaId));
    if (!p || p.status === 'EM_VOTACAO' || p.status === 'PENDENTE') return;
    if (voltarOrdemRef.current) clearTimeout(voltarOrdemRef.current);
    const map = {}; const cnt = { SIM: 0, NAO: 0, ABSTENCAO: 0 };
    (p.votos || []).forEach(v => { if (v.vereadorId) map[v.vereadorId] = v.opcao; if (cnt[v.opcao] !== undefined) cnt[v.opcao]++; });
    setPautaAtiva(p); setVotosMap(map); setContagem(cnt);
  }, [conducaoTV, pautas]);
  const modoAnteriorRef = useRef(conducaoTV.modo);
  useEffect(() => {
    const antes = modoAnteriorRef.current; modoAnteriorRef.current = conducaoTV.modo;
    if (antes === 'RESULTADO' && conducaoTV.modo !== 'RESULTADO') setPautaAtiva(pa => (pa && pa.status !== 'EM_VOTACAO' ? null : pa));
  }, [conducaoTV.modo]);
  // <<< PLENARIUM:CONDUCAO
  // >>> PLENARIUM:TV_CAMARA -- o painel e de UMA camara: a do endereco. Se o navegador
  // estiver logado em outra camara (e nao for o super admin), nao busca nada e mostra
  // o aviso; antes a TV juntava a sessao de uma camara com os vereadores da outra.
  const usuarioTV = React.useMemo(() => { try { return JSON.parse(localStorage.getItem('usuario') || 'null'); } catch (e) { return null; } }, []);
  const conflitoCamara = !!((camaraIdParam || slug) && camaraId && usuarioTV && !usuarioTV.superAdmin && usuarioTV.camaraId && usuarioTV.camaraId !== camaraId);
  // <<< PLENARIUM:TV_CAMARA

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (slug && !camaraIdParam) {
      axios.get(`/api/publica/camara/${slug}`).then(res => setCamaraId(res.data.id)).catch(() => {});
    } else if (!camaraIdParam && !slug) {
      const usuario = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (usuario?.camaraId) setCamaraId(usuario.camaraId);
    }
  }, [slug, camaraIdParam]);

  const carregar = useCallback(async () => {
    const meu = ++seqCarregarRef.current; const inicio = Date.now(); // PLENARIUM:TV_TEMPO_REAL
    try {
      const headers = getHeaders();
      const url = `/api/sessoes/em-andamento${camaraId ? `?camaraId=${camaraId}` : ''}`;
      // >>> PLENARIUM:AJUSTES_2110 -- vereadores e sessao separados: se o acesso da TV vencer, a galeria dos
      // vereadores continua (lista publica) e a tela avisa que precisa entrar de novo
      const [sR, vR] = await Promise.allSettled([
        axios.get(url, { headers }),
        axios.get(`/api/vereadores${camaraId ? `?camaraId=${camaraId}` : ''}`, { headers })
          .catch(e => { if (camaraId) return axios.get(`/api/publica/camara/${camaraId}/vereadores`).then(r => ({ data: (r.data || []).map(v => ({ ativo: true, ...v })) })); throw e; })
      ]);
      if (meu !== seqCarregarRef.current) return; // PLENARIUM:TV_TEMPO_REAL -- chegou depois de uma recarga mais nova: ignora
      if (vR.status === 'fulfilled' && Array.isArray(vR.value.data)) setVereadores(vR.value.data.filter(v => v.ativo));
      if (sR.status === 'rejected') { const st = sR.reason && sR.reason.response && sR.reason.response.status; setLoginExpirado(st === 401 || st === 403); throw sR.reason; }
      setLoginExpirado(false);
      const sRes = sR.value;
      // <<< PLENARIUM:AJUSTES_2110
      sessaoAtualRef.current = sRes.data ? sRes.data.id : null;
      setSessao(sRes.data);
      // Buscar presenças da sessão em andamento
      if (sRes.data?.id) {
        try {
          const pRes = await axios.get(`/api/presencas/painel/${sRes.data.id}`, { headers }).catch(() => axios.get(`/api/relatorios/sessao/${sRes.data.id}`, { headers })); // PLENARIUM:AJUSTES_2110 -- lista leve
          if (meu !== seqCarregarRef.current) return; // PLENARIUM:TV_TEMPO_REAL
          setPresencas(pRes.data.presenca || []);
          setChamada2TV(pRes.data.segundaChamada || null); // PLENARIUM:DUAS_CHAMADAS
        } catch { setPresencas([]); }
      }
      if (sRes.data) {
        const ord = [...(sRes.data.pautas || [])].sort((a, b) => a.ordem - b.ordem);
        setPautas(ord);
        const ativa = ord.find(p => p.status === 'EM_VOTACAO');
        if (ativa) {
          setPautaAtiva(ativa);
          const map = {}; const cnt = { SIM: 0, NAO: 0, ABSTENCAO: 0 };
          ativa.votos?.forEach(v => { if (v.vereadorId) map[v.vereadorId] = v.opcao; cnt[v.opcao]++; });
          (ativa.votacaoSecreta ? (ativa.votantes || []) : []).forEach(id => { map[id] = 'VOTOU'; }); // PLENARIUM:SEGURANCA -- secreta: so quem votou
          // PLENARIUM:TV_TEMPO_REAL -- se um voto chegou ao vivo depois que esta recarga comecou, a foto do servidor
          // ja esta velha: mantem o que esta na tela (antes os votos "voltavam" e eram contados de novo)
          if (ultimoVotoAoVivoRef.current <= inicio) { setVotosMap(map); setContagem(cnt); }
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [camaraId]);

  useEffect(() => { // PLENARIUM:AJUSTES_2110 -- acesso vencido: tenta de novo a cada 30 s (volta sozinha quando entrar de novo)
    if (!loginExpirado) return undefined;
    const t = setInterval(() => carregar(), 30000);
    return () => clearInterval(t);
  }, [loginExpirado, carregar]);

  useEffect(() => {
    if (conflitoCamara) { setLoading(false); return; } // PLENARIUM:TV_CAMARA
    if (camaraId !== null) carregar();
    else if (!slug && !camaraIdParam) carregar();
  }, [camaraId, carregar, conflitoCamara, slug, camaraIdParam]);

  useEffect(() => {
    if (camaraId) {
      axios.get(`/api/publica/camara/id/${camaraId}`).then(r => setCamaraInfo(r.data)).catch(() => {});
    }
  }, [camaraId]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    socket.on('connect', () => {
      socket.emit('entrar-sala', `sessao-${sessaoId}`);
      socket.emit('cronometro-sync', { sessaoId });
      // Diz ao Painel do Servidor que esta TV está ligada.
      socket.emit('identificar', { tipo: 'painel', nome: 'Painel de TV' });
    });
    socket.on('pauta-iniciada', (p) => {
      if (voltarOrdemRef.current) clearTimeout(voltarOrdemRef.current);
      setPautaAtiva(p); setContagem({ SIM: 0, NAO: 0, ABSTENCAO: 0 });
      { const m0 = {}; (p.votacaoSecreta ? (p.votantes || []) : []).forEach(id => { m0[id] = 'VOTOU'; }); setVotosMap(m0); } // PLENARIUM:SEGURANCA
      // PLENARIUM:VOTACAO_BLOCO -- marca tambem as outras materias do bloco
      const idsBloco = new Set((p.bloco || []).map(b => b.id));
      setPautas(prev => prev.map(x => x.id === p.id ? { ...x, status: 'EM_VOTACAO', blocoId: p.blocoId || null } : idsBloco.has(x.id) ? { ...x, blocoId: p.id } : (x.status === 'EM_VOTACAO' ? { ...x, status: 'PENDENTE' } : x)));
    });
    socket.on('pauta-encerrada', (p) => {
      setPautaAtiva(p);
      const map = {}; const cnt = { SIM: 0, NAO: 0, ABSTENCAO: 0 };
      p.votos?.forEach(v => { if (v.vereadorId) map[v.vereadorId] = v.opcao; cnt[v.opcao]++; });
      (p.votacaoSecreta ? (p.votantes || []) : []).forEach(id => { map[id] = 'VOTOU'; }); // PLENARIUM:SEGURANCA -- secreta: placar sem donos
      setVotosMap(map); setContagem(cnt);
      // PLENARIUM:VOTACAO_BLOCO -- o resultado vale para as outras materias do bloco
      const blocoEnc = {}; (p.bloco || []).forEach(b => { blocoEnc[b.id] = b; });
      setPautas(prev => prev.map(x => x.id === p.id ? { ...x, status: p.status, votos: p.votos } : blocoEnc[x.id] ? { ...x, status: blocoEnc[x.id].status, votos: blocoEnc[x.id].votos, blocoId: p.id } : x));
      if (voltarOrdemRef.current) clearTimeout(voltarOrdemRef.current);
      voltarOrdemRef.current = setTimeout(() => { setPautaAtiva(null); }, 10000);
    });
    socket.on('novo-voto', (data) => {
      ultimoVotoAoVivoRef.current = Date.now(); // PLENARIUM:TV_TEMPO_REAL
      if (data.voto && !data.votacaoSecreta) setVotosMap(prev => ({ ...prev, [data.voto.vereadorId]: data.voto.opcao }));
      if (data.votacaoSecreta && data.votanteId) setVotosMap(prev => ({ ...prev, [data.votanteId]: 'VOTOU' })); // PLENARIUM:SEGURANCA
      const cnt = { SIM: 0, NAO: 0, ABSTENCAO: 0 };
      data.contagem.forEach(c => { cnt[c.opcao] = c._count.opcao; });
      setContagem(cnt);
    });
    socket.on('cronometro-update', (data) => {
      if (data.encerrado) {
        // Só anuncia se a contagem local ainda não tiver anunciado este mesmo
        // cronômetro — evita som e mensagem em dobro.
        const jaAnunciado = anuncioRef.current === (data.nome || 'crono');
        anuncioRef.current = data.nome || 'crono';
        if (jaAnunciado) {
          if (cronIntervalRef.current) clearInterval(cronIntervalRef.current);
          setTempoLocal(0);
          return;
        }
        // Mostrar overlay ANTES de limpar o cronômetro
        setTempoEncerrado(true);
        if (encerradoTimerRef.current) clearTimeout(encerradoTimerRef.current);
        encerradoTimerRef.current = setTimeout(() => {
          setTempoEncerrado(false);
          setCronometro(null);
        }, 3000);
        // Som de encerramento
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          [880, 660, 440].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.5, ctx.currentTime + i * 0.2);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.2 + 0.2);
            osc.start(ctx.currentTime + i * 0.2);
            osc.stop(ctx.currentTime + i * 0.2 + 0.2);
          });
        } catch(e) {}
        setTempoLocal(0);
        if (cronIntervalRef.current) clearInterval(cronIntervalRef.current);
      } else if (data.pausado) {
        // PAUSADO: congela o tempo local e para a contagem
        if (cronIntervalRef.current) clearInterval(cronIntervalRef.current);
        setCronometro(data);
        setTempoLocal(data.tempo);
      } else {
        // RODANDO: o servidor manda o tempo certo e a tela CONTA SOZINHA a
        // partir dele. Cada aviso que chega corrige o valor — assim o número
        // não congela quando a rede engasga.
        setCronometro(data);
        setTempoLocal(data.tempo);
        anuncioRef.current = null; // cronômetro novo: pode anunciar de novo
        if (cronIntervalRef.current) clearInterval(cronIntervalRef.current);
        cronIntervalRef.current = setInterval(() => {
          setTempoLocal(t => (t > 0 ? t - 1 : 0));
        }, 1000);
      }
    });
    // >>> PLENARIUM:TV_TEMPO_REAL
    // Presenca: busca SO a lista de presenca (antes recarregava a sessao inteira e as fotos de
    // todos os vereadores a cada confirmacao e a cada voto -- lento e fazia o placar piscar).
    // Varios avisos seguidos viram uma busca so; vale sempre a resposta mais nova.
    let tRecarga = null; let tPresenca = null; let seqPresenca = 0;
    const recarregar = () => { clearTimeout(tRecarga); tRecarga = setTimeout(() => carregar(), 150); };
    const atualizarPresenca = (d) => {
      // PLENARIUM:AJUSTES_2110 -- o aviso ja diz quem confirmou: a TV pinta na hora (como o voto)
      // e depois confere com a lista leve do servidor. Na 2a chamada so confere.
      if (d && d.vereadorId && d.presente === true && d.sessaoId === sessaoAtualRef.current && !chamada2Ref.current) {
        setPresencas(prev => (prev.length ? prev.map(p => (p.id === d.vereadorId ? { ...p, presente: true } : p)) : prev));
      }
      clearTimeout(tPresenca);
      tPresenca = setTimeout(async () => {
        const id = sessaoAtualRef.current;
        if (!id) { recarregar(); return; }
        const meu = ++seqPresenca;
        try {
          const r = await axios.get(`/api/presencas/painel/${id}`, { headers: getHeaders() }).catch(() => axios.get(`/api/relatorios/sessao/${id}`, { headers: getHeaders() })); // PLENARIUM:AJUSTES_2110 -- lista leve
          if (meu !== seqPresenca) return;
          setPresencas(r.data.presenca || []);
          setChamada2TV(r.data.segundaChamada || null);
        } catch (e) { recarregar(); }
      }, 120);
    };
    socket.on('sessao-atualizada', recarregar);
    socket.on('bloco-atualizado', recarregar); // PLENARIUM:VOTO_MESA -- destaque
    socket.on('presenca-atualizada', atualizarPresenca);
    socket.on('chamada-atualizada', atualizarPresenca);
    // <<< PLENARIUM:TV_TEMPO_REAL
    socket.on('vereador-foto-atualizada', ({ vereadorId, fotoUrl }) => {
      setVereadores(prev => prev.map(v => v.id === vereadorId ? { ...v, fotoUrl } : v));
    });
    return () => {
      clearTimeout(tRecarga); clearTimeout(tPresenca); // PLENARIUM:TV_TEMPO_REAL
      socket.disconnect();
      if (cronIntervalRef.current) clearInterval(cronIntervalRef.current);
      if (encerradoTimerRef.current) clearTimeout(encerradoTimerRef.current);
    };
  }, [sessaoId, carregar]);

  // Detectar quando tempo zera para mostrar mensagem e tocar som
  React.useEffect(() => {
    if (tempoLocal === 0 && cronometro && !tempoEncerrado) {
      // Mesma proteção do lado do socket: anuncia uma vez só.
      if (anuncioRef.current === (cronometro.nome || 'crono')) return;
      anuncioRef.current = cronometro.nome || 'crono';
      if (cronIntervalRef.current) clearInterval(cronIntervalRef.current);
      setTempoEncerrado(true);
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [880, 660, 440].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.5, ctx.currentTime + i * 0.18);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.18);
          osc.start(ctx.currentTime + i * 0.18);
          osc.stop(ctx.currentTime + i * 0.18 + 0.18);
        });
      } catch(e) {}
      encerradoTimerRef.current = setTimeout(() => setTempoEncerrado(false), 3000);
    }
  }, [tempoLocal]);

  // PLENARIUM:SEGURANCA -- na secreta aberta o placar nao chega: conta quem ja votou
  const secretaAberta = !!(pautaAtiva && pautaAtiva.votacaoSecreta && pautaAtiva.status === 'EM_VOTACAO');
  const totalVotos = secretaAberta ? Object.keys(votosMap).length : contagem.SIM + contagem.NAO + contagem.ABSTENCAO;
  // Grade da votação: mesma regra da tela de presença — sempre duas linhas,
  // com o card dimensionado pela altura disponível (nunca transborda).
  const nVot = vereadores.length || 1;
  const colunasV = nVot <= 5 ? nVot : Math.ceil(nVot / 2);
  const linhasV = Math.ceil(nVot / colunasV);
  const alturaV = ((86 - 12) / linhasV) - 2; // PLENARIUM:TV_TITULO -- so ate a grade ser medida (ver areaV abaixo)
  const larguraV = (alturaV - 11) * 0.75;
  // >>> PLENARIUM:GRADE_CABER inicio (votacao)
  // A largura tambem limita. A grade da votacao tem, na horizontal, a tela
  // menos 58vh (placar 52vh + margens 4vh + espaco 2vh), menos o espaco
  // entre os cards e 2vh de folga. O card fica com a MENOR largura: a que
  // vem da altura (como antes) ou a que cabe. Sem isto, 11 vereadores numa
  // tela 16:9 quebravam em 3 linhas e a grade cortava.
  // PLENARIUM:TV_TITULO v2 -- o titulo da materia ocupa o espaco que precisar; a grade
  // mede o espaco que sobrou (abaixo dele) e escolhe o tamanho dos cards para caber
  // sempre nas mesmas fileiras de antes.
  const roGradeRef = useRef(null);
  const [areaV, setAreaV] = useState(null);
  const areaGradeRef = useCallback((el) => {
    if (roGradeRef.current) { roGradeRef.current.disconnect(); roGradeRef.current = null; }
    if (!el) return;
    const medir = () => {
      const r = el.getBoundingClientRect();
      setAreaV(a => (a && Math.abs(a.w - r.width) < 1 && Math.abs(a.h - r.height) < 1) ? a : { w: r.width, h: r.height });
    };
    medir();
    if (typeof ResizeObserver !== 'undefined') { roGradeRef.current = new ResizeObserver(medir); roGradeRef.current.observe(el); }
  }, []);
  let cardPx = null;
  let proporcaoFoto = '3/4'; // PLENARIUM:TV_FOTOS -- muda para preencher o espaco (ate quadrada)
  const [infoTV, setInfoTV] = useState(null); // altura real da parte de baixo do card (nome, partido, etiqueta)
  React.useLayoutEffect(() => {
    const el = document.querySelector('[data-tv-info]');
    if (!el) return;
    const hh = Math.round(el.getBoundingClientRect().height);
    if (hh > 0 && (infoTV === null || Math.abs(infoTV - hh) > 1)) setInfoTV(hh);
  }, [infoTV, pautaAtiva, vereadores.length, areaV]);
  let larguraVCss = `min(${larguraV}vh, calc((100vw - ${58 + (colunasV - 1) * 1.4 + 2}vh) / ${colunasV}))`;
  let maxGradeVCss = `calc(${colunasV} * ${larguraVCss} + ${(colunasV - 1) * 1.4 + 0.5}vh)`;
  if (areaV && areaV.h > 0) {
    const vh = window.innerHeight / 100; const espaco = 1.4 * vh;
    const info = (infoTV || 10.5 * vh) + 0.8 * vh; // + bordas do card
    const altCard = (areaV.h - (linhasV - 1) * espaco) / linhasV;
    const fotoH = Math.max(40, altCard - info);
    const pelaLargura = Math.floor((areaV.w - (colunasV - 1) * espaco - 4) / colunasV);
    let larg;
    if (fotoH * 0.75 >= pelaLargura) larg = Math.max(40, pelaLargura); // falta largura: foto 3:4
    else { // sobra largura: a foto fica mais larga (ate quadrada) e ocupa a altura toda
      larg = Math.max(40, Math.min(pelaLargura, Math.floor(fotoH)));
      proporcaoFoto = `${larg} / ${Math.floor(fotoH)}`;
    }
    larguraVCss = `${larg}px`; cardPx = larg;
    maxGradeVCss = `${colunasV * larg + (colunasV - 1) * espaco + 6}px`;
  }
  // <<< PLENARIUM:GRADE_CABER fim (votacao)
  const totalVereadores = vereadores.length;
  // Presentes = vereadores com registro de presença na sessão atual
  const totalPresentes = presencas.length > 0
    ? presencas.filter(p => p.presente !== false).length
    : 0;
  const tipoQuorum = sessao?.tipoQuorum || 'MAIORIA_ABSOLUTA';
  // O presidente conta como presente, mas não entra na base de cálculo do
  // quórum (ele só vota em caso de empate — voto de minerva).
  const presidenteVereador = vereadores.find(v => v.cargo === 'PRESIDENTE');
  const presidentePresente = presidenteVereador
    ? (presencas.length > 0 ? !!presencas.find(p => p.id === presidenteVereador.id)?.presente : true)
    : false;
  const totalVereadoresVotantes = vereadores.filter(v => v.cargo !== 'PRESIDENTE').length;
  const totalPresentesVotantes = totalPresentes - (presidentePresente ? 1 : 0);
  // PLENARIUM:QUORUM_CAMARA -- numero fixo da camara: abertura ate a Ordem do Dia, depois o de votacao
  const quorumMinimo = quorumDaCamara(estruturaTV, sessao && (sessao.ordemDoDiaIniciada || chamada2TV) ? 'votacao' : 'abertura' /* PLENARIUM:QUORUM_AJUSTE -- 2a chamada ja usa o quorum de votacao */, tipoQuorum === 'MAIORIA_SIMPLES'
    ? Math.floor(totalPresentesVotantes / 2) + 1
    : Math.floor(totalVereadoresVotantes / 2) + 1);
  const maioriaNecessaria = Math.floor(totalPresentesVotantes / 2) + 1;

  const fmt = (seg) => {
    const m = Math.floor(seg / 60).toString().padStart(2, '0');
    const sc = (seg % 60).toString().padStart(2, '0');
    return `${m}:${sc}`;
  };

  const corCrono = cronometro
    ? tempoLocal > 60 ? '#22c55e' : tempoLocal > 30 ? '#eab308' : '#ef4444'
    : '#22c55e';

  // PLENARIUM:VOTACAO_BLOCO -- outras materias do bloco em votacao (se houver)
  const membrosBlocoTV = pautaAtiva ? (pautaAtiva.bloco || pautas.filter(x => x.blocoId && x.blocoId === pautaAtiva.id && x.id !== pautaAtiva.id && (x.status === 'PENDENTE' || x.status === pautaAtiva.status))) : [];
  const itensBlocoTV = membrosBlocoTV.length ? [pautaAtiva, ...membrosBlocoTV] : [];

  const pautasVotadas   = pautas.filter(p => ['APROVADA','REPROVADA','RETIRADA'].includes(p.status));
  const pautasPendentes = pautas.filter(p => p.status === 'PENDENTE');
  const pautasOD = pautas.filter(p => p.tipo !== 'ATA_ANTERIOR'); // PLENARIUM:AJUSTES_2110 -- Ordem do Dia sem a ata da sessao anterior

  // Overlay global de tempo encerrado
  if (conflitoCamara) return ( // PLENARIUM:TV_CAMARA
    <div style={{ width: '100vw', height: '100vh', background: 'linear-gradient(160deg,#0a1f3d,#0d1424)', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2.4vh', padding: '4vh', boxSizing: 'border-box', textAlign: 'center' }}>
      {camaraInfo?.logoUrl && <img src={camaraInfo.logoUrl} alt="Brasão" style={{ width: '12vh', height: '12vh', objectFit: 'contain' }} />}
      <div style={{ fontSize: '2vh', fontWeight: 800, letterSpacing: '0.3vh', color: '#fde68a' }}>PAINEL DE OUTRA CÂMARA</div>
      <div style={{ fontSize: '4.2vh', fontWeight: 900, lineHeight: 1.2, maxWidth: '90vw' }}>Este painel é da {camaraInfo?.nome || 'Câmara do endereço'}</div>
      <div style={{ fontSize: '2.4vh', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, maxWidth: '80vh' }}>
        Este navegador está conectado como <b>{usuarioTV?.nome || 'outro usuário'}</b>{usuarioTV?.camara?.nome ? <> — {usuarioTV.camara.nome}</> : ''}.<br />
        Para exibir o painel, entre com um usuário desta Câmara.
      </div>
      <button type="button" onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('usuario'); window.location.href = '/login/admin'; }}
        style={{ marginTop: '1vh', fontSize: '2.2vh', fontWeight: 800, color: '#fff', background: '#2563eb', border: 'none', borderRadius: '1.2vh', padding: '1.6vh 3.2vh', cursor: 'pointer' }}>
        Sair e entrar com o usuário desta Câmara
      </button>
    </div>
  );

  if (tempoEncerrado && !tvManual) return ( // PLENARIUM:TV_SOBERANA
    <div style={{ width: '100vw', height: '100vh', background: 'rgba(180,0,0,0.95)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
      <div style={{ fontSize: 80 }}>⏰</div>
      <h1 style={{ fontSize: 100, fontWeight: 900, color: 'white', margin: 0, textTransform: 'uppercase', letterSpacing: 8 }}>TEMPO ENCERRADO</h1>
      {cronometro && <p style={{ fontSize: 36, color: 'rgba(255,255,255,0.75)', margin: 0 }}>{exibPorNome(vereadores, cronometro.nome) || 'Orador'}</p>}
    </div>
  );

  if (loading) return (
    <div className="painel-tv" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (!sessao) return <AguardandoTV camara={camaraInfo} vereadores={vereadores} nomeDe={exibCurto} aviso={loginExpirado ? 'O acesso deste painel expirou. Entre novamente no sistema neste navegador para a sessão aparecer aqui.' : ''} />; // PLENARIUM:TV_AGUARDANDO -- tela antes da sessao (visual novo)

  const Header = () => (
    <div style={{
      background: 'linear-gradient(90deg,#071020 0%,#0d1f3c 100%)',
      borderBottom: '3px solid #1a3260',
      padding: `${s(10)} ${s(28)}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexShrink: 0, height: s(80),
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: s(16) }}>
        {sessao?.camara?.logoUrl && (
          <img src={sessao.camara.logoUrl} alt="Brasão" style={{ width: s(60), height: s(60), objectFit: 'contain', filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.7))' }} />
        )}
        <div>
          <h1 style={{ fontSize: s(26), fontWeight: 900, margin: 0, textTransform: 'uppercase', letterSpacing: 1.5, lineHeight: 1 }}>
            {sessao?.camara?.nome || 'Câmara Municipal'}
          </h1>
          <p style={{ fontSize: s(11), opacity: 0.4, margin: `${s(4)} 0 0`, textTransform: 'uppercase', letterSpacing: 2 }}>PODER LEGISLATIVO</p>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: s(28) }}>
        {cronometro && (
          <div style={{ textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.1)', paddingRight: s(28) }}>
            <div style={{ fontSize: s(11), color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 2, marginBottom: s(2) }}>TEMPO:</div>
            <div style={{ fontFamily: 'monospace', fontSize: s(36), fontWeight: 900, color: corCrono, letterSpacing: 4, lineHeight: 1 }}>{fmt(tempoLocal)}</div>
          </div>
        )}
        <Relogio />
      </div>
    </div>
  );

  // ── MODO CRONÔMETRO ──
  // ── CRONOMETRO EM DUAS COLUNAS ──────────────────────────────────────
  // Foto grande à esquerda com o nome abaixo, tempo grande à direita.
  // A Ordem do Dia saiu da lateral: já tem tela própria.
  if (cronometro && !tvManual) return ( // PLENARIUM:TV_SOBERANA
    <div className="painel-tv" style={{ position: 'relative' }}>
      <EleicaoMesaTV sessaoId={sessao?.id} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} sessaoTitulo={sessao?.titulo} vereadores={vereadores} />
      {tempoEncerrado && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(180,0,0,0.93)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ fontSize: s(50), marginBottom: s(16) }}>⏰</div>
          <h1 style={{ fontSize: s(90), fontWeight: 900, color: 'white', margin: 0, textTransform: 'uppercase', letterSpacing: s(4) }}>TEMPO ENCERRADO</h1>
          <p style={{ fontSize: s(28), color: 'rgba(255,255,255,0.75)', margin: `${s(16)} 0 0` }}>{exibPorNome(vereadores, cronometro?.nome) || 'Orador'}</p>
        </div>
      )}

      <Header />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1vh', padding: '2vh', overflow: 'hidden', boxSizing: 'border-box' }}>

        {/* Esquerda: foto e nome */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          {cronometro.foto
            ? <img src={cronometro.foto} alt="" style={{ height: '48vh', width: 'auto', maxWidth: '36vw', borderRadius: '2vh', objectFit: 'contain', background: 'rgba(255,255,255,0.05)', border: `0.6vh solid ${corCrono}`, boxShadow: `0 0 6vh ${corCrono}55` }} />
            : <div style={{ height: '48vh', width: '36vh', borderRadius: '2vh', background: 'rgba(255,255,255,0.06)', border: `0.6vh solid ${corCrono}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14vh', fontWeight: 900, color: 'rgba(255,255,255,0.3)' }}>
                {exibPorNome(vereadores, cronometro.nome).split(' ').map(n => n[0]).slice(0,2).join('') || '🎙️'}
              </div>}
          <div style={{ fontSize: '2.2vh', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.4vh', margin: '1.6vh 0 0.4vh', fontWeight: 700 }}>USO DA PALAVRA</div>
          <div style={{ fontSize: '3.8vh', fontWeight: 900, color: 'white', textAlign: 'center', lineHeight: 1.1 }}>
            {exibPorNome(vereadores, cronometro.nome) || 'Orador'}
          </div>
        </div>

        {/* Direita: o tempo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '14vh', fontWeight: 900, color: cronometro?.pausado ? 'rgba(255,255,255,0.3)' : corCrono, lineHeight: 1, letterSpacing: '1.5vh', textShadow: cronometro?.pausado ? 'none' : `0 0 8vh ${corCrono}77`, transition: 'color 0.3s' }}>
            {fmt(tempoLocal)}
          </div>
          {cronometro?.pausado && (
            <div style={{ fontSize: '3.4vh', fontWeight: 900, color: '#fbbf24', letterSpacing: '0.6vh', marginTop: '2vh' }}>PAUSADO</div>
          )}
        </div>
      </div>
    </div>
  );

  // ── TELA DE PRESENCA ────────────────────────────────────────────────
  // Primeiro momento da sessão: enquanto o administrador não declara o
  // início da Ordem do Dia, o telão mostra só a chamada de presença.
  // Layout em duas colunas — grade à esquerda, informações à direita —
  // porque com a grade ocupando a largura toda as fotos ficavam pequenas
  // ou a segunda linha era cortada.
  // PLENARIUM:TV_SOBERANA -- documento da materia na TV
  if (sessao && conducaoTV.modo === 'MATERIA' && conducaoTV.dados) return <MateriaTV dados={conducaoTV.dados} camara={{ ...(camaraInfo || {}), ...(sessao.camara || {}) }} headers={getHeaders()} camaraId={camaraId} />;
  // >>> PLENARIUM:CONDUCAO -- tela da fase (votacao e cronometro continuam por cima, sozinhos)
  if (sessao && (!pautaAtiva || esconderVotacao) && conducaoTV.modo === 'FASE' && conducaoTV.dados && conducaoTV.dados.titulo) return ( // PLENARIUM:TV_SOBERANA
    <TelaFaseTV camara={{ ...(camaraInfo || {}), ...(sessao.camara || {}) }} sessao={sessao} titulo={conducaoTV.dados.titulo} subtitulo={conducaoTV.dados.subtitulo} presentes={totalPresentes} total={totalVereadores} minimo={quorumMinimo}>
        <EleicaoMesaTV sessaoId={sessao?.id} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} sessaoTitulo={sessao?.titulo} vereadores={vereadores} />
        <LeituraTV sessaoId={sessao?.id} oculto={(!!pautaAtiva && pautaAtiva.status === 'EM_VOTACAO') || tvManual} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} brasao={camaraInfo?.logoUrl || sessao?.camara?.logoUrl || ''} headers={getHeaders()} />
        <TribunaTV sessaoId={sessao?.id} oculto={(!!pautaAtiva && pautaAtiva.status === 'EM_VOTACAO') || !!cronometro || tvManual} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} />
    </TelaFaseTV>
  );
  // <<< PLENARIUM:CONDUCAO
  if (sessao && (!pautaAtiva || esconderVotacao) && (conducaoTV.modo === 'PRESENCA' || (!sessao.ordemDoDiaIniciada && conducaoTV.modo !== 'ORDEM_DIA'))) { // PLENARIUM:TV_SOBERANA // PLENARIUM:CONDUCAO (antes: so enquanto a Ordem do Dia nao comecava)
    const presentesIds = new Set((presencas || []).filter(p => p.presente !== false).map(p => p.id));
    const temQuorum = totalPresentes >= quorumMinimo;
    const brasao = sessao?.camara?.logoUrl || camaraInfo?.logoUrl;
    const nomeCamara = sessao?.camara?.nome || camaraInfo?.nome || 'Câmara Municipal';
    // A grade se dimensiona sozinha: escolhe as colunas pelo total e
    // divide a altura disponivel pelas linhas, para nunca transbordar.
    const nV = vereadores.length || 1;
    const colunas = nV <= 5 ? nV : Math.ceil(nV / 2);
    const linhas = Math.ceil(nV / colunas);
    // 92vh de area util dividida pelas linhas, menos o espaco entre elas
    const alturaCard = (92 / linhas) - 2;
    // o card e 3:4 mais o rodape de texto (~9vh), entao a largura sai daqui
    const larguraCard = (alturaCard - 11) * 0.75;
    // >>> PLENARIUM:GRADE_CABER inicio (presenca)
    // A largura tambem limita. A coluna da grade tem a tela menos 37,5vh
    // (margens 5vh + coluna da direita 30vh + espaco 2,5vh), menos o espaco
    // entre os cards e 1vh de folga. O card fica com a MENOR largura: a que
    // vem da altura (como antes) ou a que cabe. Sem isto, 11 vereadores numa
    // tela 16:9 quebravam em 3 linhas e a grade cortava em cima e embaixo.
    const larguraCardCss = `min(${larguraCard}vh, calc((100vw - ${37.5 + (colunas - 1) * 1.6 + 1}vh) / ${colunas}))`;
    const maxGradeCss = `calc(${colunas} * ${larguraCardCss} + ${(colunas - 1) * 1.6 + 0.5}vh)`;
    // <<< PLENARIUM:GRADE_CABER fim (presenca)
    // PLENARIUM:LEITURAS -- cargos da Mesa Diretora
    const cargoDe = (v) => v.cargo === 'PRESIDENTE' ? 'Presidente da Câmara'
      : v.cargo === 'VICE_PRESIDENTE' ? 'Vice-Presidente'
      : v.cargo === 'SEGUNDO_VICE_PRESIDENTE' ? '2º Vice-Presidente'
      : v.cargo === 'PRIMEIRO_SECRETARIO' ? (v.sexo === 'F' ? '1ª Secretária' : '1º Secretário')
      : v.cargo === 'SEGUNDO_SECRETARIO' ? (v.sexo === 'F' ? '2ª Secretária' : '2º Secretário')
      : v.cargo === 'TESOUREIRO' ? (v.sexo === 'F' ? 'Tesoureira' : 'Tesoureiro')
      : tratamento(v);
    return (
      <div style={{ height: '100vh', background: 'linear-gradient(160deg, #0a1628 0%, #0f2847 55%, #0a1628 100%)', color: 'white', padding: '2.5vh', display: 'flex', gap: '2.5vh', boxSizing: 'border-box', overflow: 'hidden' }}>
        <EleicaoMesaTV sessaoId={sessao?.id} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} sessaoTitulo={sessao?.titulo} vereadores={vereadores} />
        <LeituraTV sessaoId={sessao?.id} oculto={(!!pautaAtiva && pautaAtiva.status === 'EM_VOTACAO') || tvManual} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} brasao={camaraInfo?.logoUrl || sessao?.camara?.logoUrl || ''} headers={getHeaders()} /> {/* PLENARIUM:LEITURAS */}
        <TribunaTV sessaoId={sessao?.id} oculto={(!!pautaAtiva && pautaAtiva.status === 'EM_VOTACAO') || !!cronometro || tvManual} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} /> {/* PLENARIUM:TRIBUNA */}

        {/* Coluna esquerda: grade de vereadores */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.6vh', justifyContent: 'center', maxWidth: maxGradeCss, margin: '0 auto' }}>
            {vereadores.map(v => {
              const presente = presentesIds.has(v.id);
              return (
                <div key={v.id} style={{ flex: '0 0 auto', width: larguraCardCss, background: presente ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.03)', border: `0.35vh solid ${presente ? 'rgba(34,197,94,0.65)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '1.2vh', overflow: 'hidden', opacity: presente ? 1 : 0.5, transition: 'all 0.4s ease', boxSizing: 'border-box' }}>
                  <div style={{ width: '100%', aspectRatio: '3/4', background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                    {v.fotoUrl
                      ? <img src={v.fotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', filter: presente ? 'none' : 'grayscale(100%)' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4vh', fontWeight: 700, color: 'rgba(255,255,255,0.25)' }}>{exib(v).split(' ').map(n=>n[0]).join('').slice(0,2)}</div>}
                  </div>
                  <div style={{ padding: '0.9vh 0.6vh', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.3vh', color: 'rgba(255,255,255,0.5)', marginBottom: '0.3vh', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cargoDe(v)}</div>
                    <div style={{ fontSize: '1.9vh', fontWeight: 800, lineHeight: 1.1, marginBottom: '0.3vh', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{exibCurto(v)}</div>
                    <div style={{ fontSize: '1.6vh', color: 'rgba(255,255,255,0.45)', marginBottom: '0.5vh' }}>{v.partido || ''}</div>
                    {/* PLENARIUM:TV_PRESENTE_SELO -- selo moderno: verde cheio com check / contorno com relogio */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.7vh', margin: '0 auto', maxWidth: '100%', boxSizing: 'border-box', padding: '0.45vh 1.2vh', borderRadius: 999, fontSize: '1.45vh', fontWeight: 900, letterSpacing: '0.12vh', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden',
                      background: presente ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : 'rgba(255,255,255,0.05)',
                      border: `0.18vh solid ${presente ? 'rgba(134,239,172,0.7)' : 'rgba(255,255,255,0.18)'}`,
                      color: presente ? '#ffffff' : 'rgba(255,255,255,0.55)',
                      boxShadow: presente ? '0 0.4vh 1.4vh rgba(34,197,94,0.35)' : 'none' }}>
                      {presente
                        ? <svg width="1.7vh" height="1.7vh" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="12" fill="#ffffff" /><path d="M6.8 12.6l3.4 3.4L17.4 8.6" fill="none" stroke="#16a34a" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        : <svg width="1.7vh" height="1.7vh" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.2 2" /></svg>}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{presente ? 'PRESENTE' : 'AGUARDANDO'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Coluna direita: identificação e números */}
        <div style={{ flex: '0 0 30vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2vh', background: 'rgba(255,255,255,0.03)', borderRadius: '1.5vh', border: '0.2vh solid rgba(255,255,255,0.07)', padding: '2.5vh 2vh', boxSizing: 'border-box' }}>
          <div style={{ textAlign: 'center' }}>
            {brasao && <img src={brasao} alt="Brasão" style={{ height: '22vh', maxWidth: '100%', objectFit: 'contain', marginBottom: '2vh' }} />}
            <div style={{ fontSize: '2.6vh', fontWeight: 900, lineHeight: 1.2 }}>{nomeCamara}</div>
            <div style={{ height: '0.2vh', background: 'rgba(255,255,255,0.15)', margin: '1.4vh 0' }} />
            <div style={{ fontSize: '1.9vh', color: '#93c5fd', fontWeight: 700 }}>{sessao.titulo}</div>
          </div>

          <div style={{ background: 'rgba(59,130,246,0.12)', border: '0.25vh solid rgba(59,130,246,0.35)', borderRadius: '1.2vh', padding: '1.6vh', textAlign: 'center' }}>
            <div style={{ fontSize: '1.6vh', color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: '0.15vh' }}>PRESENTES</div>
            <div style={{ fontSize: '5.5vh', fontWeight: 900, lineHeight: 1.15 }}>{totalPresentes}<span style={{ fontSize: '2.6vh', color: '#93c5fd' }}> /{totalVereadores}</span></div>
          </div>

          <div style={{ background: temQuorum ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', border: `0.25vh solid ${temQuorum ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)'}`, borderRadius: '1.2vh', padding: '1.6vh', textAlign: 'center' }}>
            <div style={{ fontSize: '1.6vh', color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: '0.15vh' }}>QUÓRUM</div>
            <div style={{ fontSize: '3.2vh', fontWeight: 900, color: temQuorum ? '#4ade80' : '#f87171', lineHeight: 1.25 }}>{temQuorum ? 'ATINGIDO' : 'INSUFICIENTE'}</div>
            <div style={{ fontSize: '1.5vh', color: 'rgba(255,255,255,0.45)' }}>mínimo de {quorumMinimo}</div>
          </div>

          {/* PLENARIUM:TV_PRESENTE_SELO -- "chamada de presenca" bem visivel */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1vh', alignSelf: 'center', padding: '0.8vh 1.8vh', borderRadius: 999, background: 'rgba(255,255,255,0.08)', border: '0.18vh solid rgba(255,255,255,0.25)', fontSize: '1.6vh', fontWeight: 800, color: '#ffffff', letterSpacing: '0.2vh', whiteSpace: 'nowrap' }}>
            <span style={{ width: '1.1vh', height: '1.1vh', borderRadius: '50%', background: chamada2TV && chamada2TV.aberta ? '#fbbf24' : '#4ade80', boxShadow: `0 0 1.2vh ${chamada2TV && chamada2TV.aberta ? '#fbbf24' : '#4ade80'}`, flexShrink: 0 }} />
            {chamada2TV ? (chamada2TV.aberta ? '2ª CHAMADA DE PRESENÇA' : '2ª CHAMADA ENCERRADA') : 'CHAMADA DE PRESENÇA'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="painel-tv">
      <EleicaoMesaTV sessaoId={sessao?.id} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} sessaoTitulo={sessao?.titulo} vereadores={vereadores} />
      <LeituraTV sessaoId={sessao?.id} oculto={(!!pautaAtiva && pautaAtiva.status === 'EM_VOTACAO') || tvManual} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} brasao={camaraInfo?.logoUrl || sessao?.camara?.logoUrl || ''} headers={getHeaders()} /> {/* PLENARIUM:LEITURAS */}
      <TribunaTV sessaoId={sessao?.id} oculto={(!!pautaAtiva && pautaAtiva.status === 'EM_VOTACAO') || !!cronometro || tvManual} camaraNome={camaraInfo?.nome || sessao?.camara?.nome} /> {/* PLENARIUM:TRIBUNA */}
      <Header />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* COLUNA ESQUERDA */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '2px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>

          <div style={{ background: 'rgba(0,0,0,0.35)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: `${s(7)} ${s(20)}`, display: 'flex', alignItems: 'center', gap: s(10), flexShrink: 0 }}>
            <div style={{ width: s(9), height: s(9), borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.4s ease infinite', flexShrink: 0 }} />
            <span style={{ fontSize: s(12), fontWeight: 700, color: '#86efac', textTransform: 'uppercase', letterSpacing: 1.5, flexShrink: 0 }}>SESSÃO EM ANDAMENTO</span>
            {sessao?.titulo && (
              <>
                <div style={{ width: 1, height: s(14), background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
                <span style={{ fontSize: s(12), fontWeight: 600, color: 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sessao.titulo}</span>
              </>
            )}
          </div>

          {/* VOTACAO EM GRADE — fotos grandes, placar na lateral */}
          {pautaAtiva && !esconderVotacao ? ( /* PLENARIUM:TV_SOBERANA -- Ordem do Dia escolhida: lista, mesmo com votacao aberta */
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.8vh', padding: '2vh', overflow: 'hidden', boxSizing: 'border-box', minHeight: 0 }}>
              {/* PLENARIUM:TV_TITULO v3 -- o titulo da materia ocupa a largura toda da TV;
                  abaixo dele, as fotos e o placar comecam na mesma altura */}
                {itensBlocoTV.length ? (
                <div style={{ flexShrink: 0, textAlign: 'center', padding: '0 1vh' }}>
                  {/* PLENARIUM:VOTACAO_BLOCO -- todas as materias do bloco */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.4vh', fontSize: '1.7vh', fontWeight: 800, letterSpacing: '0.15vh', marginBottom: '1vh' }}>
                    <span style={{ color: '#fde68a' }}>VOTAÇÃO EM BLOCO · {pautaAtiva.votacaoSecreta ? 'SECRETA' : 'NOMINAL'}</span>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, letterSpacing: 0 }}>{itensBlocoTV.length} matérias · um voto para todas</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: itensBlocoTV.length > 3 ? '1fr 1fr' : '1fr', gap: '0.9vh 1.6vh', textAlign: 'left' }}>
                    {itensBlocoTV.slice(0, itensBlocoTV.length > 6 ? 5 : 6).map((p, i) => (
                      <div key={p.id} style={{ display: 'flex', gap: '1vh', alignItems: 'flex-start', background: 'rgba(255,255,255,0.05)', border: '0.15vh solid rgba(255,255,255,0.1)', borderRadius: '1vh', padding: '0.8vh 1.2vh', minWidth: 0 }}>
                        <span style={{ flex: '0 0 auto', width: '3vh', height: '3vh', borderRadius: '50%', background: '#fde68a', color: '#0a1628', fontSize: '1.6vh', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '1.45vh', fontWeight: 800, color: 'rgba(255,255,255,0.55)' }}>{TIPO_LABEL[p.tipo] || p.tipo} · {p.numero}</div>
                          <div style={{ fontSize: itensBlocoTV.length > 3 ? '2vh' : '2.5vh', fontWeight: 800, lineHeight: 1.22, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.titulo}</div>
                        </div>
                      </div>
                    ))}
                    {itensBlocoTV.length > 6 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '1vh', border: '0.15vh dashed rgba(255,255,255,0.25)', fontSize: '2vh', fontWeight: 800, color: '#fde68a' }}>+ {itensBlocoTV.length - 5} matérias</div>
                    )}
                  </div>
                </div>
                ) : (
                <div style={{ flexShrink: 0, textAlign: 'center', padding: '0 1vh' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.4vh', fontSize: '1.7vh', fontWeight: 800, letterSpacing: '0.15vh', marginBottom: '0.8vh' }}>
                    <span style={{ color: '#fde68a' }}>EM VOTAÇÃO · {pautaAtiva.votacaoSecreta ? 'SECRETA' : 'NOMINAL'}</span>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, letterSpacing: 0 }}>{TIPO_LABEL[pautaAtiva.tipo] || pautaAtiva.tipo} · {pautaAtiva.numero}</span>
                  </div>
                  <div style={{ fontSize: '3.3vh', fontWeight: 900, lineHeight: 1.25, textAlign: 'justify', textAlignLast: 'center', display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{pautaAtiva.titulo}</div>
                  {pautaAtiva.tipo === 'ATA_ANTERIOR' && pautaAtiva.retificacao && <div style={{ marginTop: '0.8vh', display: 'inline-block', background: '#f59e0b', color: '#1f2937', fontWeight: 900, fontSize: '1.7vh', letterSpacing: '0.15vh', padding: '0.4vh 1.6vh', borderRadius: 999 }}>COM RETIFICAÇÃO</div>} {/* PLENARIUM:ATA_RETIFICACAO */}
                </div>
                )}
              <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '2vh' }}>

              {/* Esquerda: os vereadores e como votaram */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div ref={areaGradeRef} style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.4vh', justifyContent: 'center', maxWidth: maxGradeVCss, margin: '0 auto' }}>
                  {vereadores.map(v => {
                    const voto = votosMap[v.id];
                    const secreta = pautaAtiva.votacaoSecreta;
                    const pv = presencas.find(p => p.id === v.id);
                    const presente = presencas.length > 0 ? (pv ? pv.presente : false) : true;
                    // Em votação secreta o painel não revela a opção — só que votou.
                    const cor = !presente ? '#475569'
                      : voto && secreta ? '#3b82f6'
                      : voto === 'SIM' ? '#22c55e'
                      : voto === 'NAO' ? '#ef4444'
                      : voto === 'ABSTENCAO' ? '#eab308'
                      : '#64748b';
                    const etiqueta = !presente ? 'AUSENTE'
                      : voto && secreta ? 'VOTOU'
                      : voto === 'SIM' ? 'SIM'
                      : voto === 'NAO' ? 'NÃO'
                      : voto === 'ABSTENCAO' ? 'ABSTENÇÃO'
                      : 'AGUARDANDO';
                    return (
                      <div key={v.id} style={{ flex: '0 0 auto', width: larguraVCss, minWidth: 0, maxWidth: larguraVCss, background: voto ? `${cor}1f` : 'rgba(255,255,255,0.03)', border: `0.35vh solid ${voto ? cor : 'rgba(255,255,255,0.08)'}`, borderRadius: '1.2vh', overflow: 'hidden', transition: 'all 0.35s ease', boxSizing: 'border-box' }}>
                        <div style={{ width: '100%', aspectRatio: proporcaoFoto, background: 'rgba(255,255,255,0.05)', overflow: 'hidden', opacity: presente ? 1 : 0.35 }}>
                          {v.fotoUrl
                            ? <img src={v.fotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: proporcaoFoto === '3/4' ? 'contain' : 'cover', objectPosition: 'center top', filter: voto ? 'none' : 'grayscale(60%)' }} />
                            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4vh', fontWeight: 700, color: 'rgba(255,255,255,0.25)' }}>{exib(v).split(' ').map(n=>n[0]).join('').slice(0,2)}</div>}
                        </div>
                        <div data-tv-info="1" style={{ padding: '0.8vh 0.7vh', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.8vh', fontWeight: 800, lineHeight: 1.1, marginBottom: '0.2vh', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: presente ? 1 : 0.55 }}>{exibCurto(v)}</div>
                          <div style={{ fontSize: '1.25vh', color: 'rgba(255,255,255,0.45)', marginBottom: '0.7vh', minHeight: '1.5vh' }}>{v.partido || ''}</div>
                          {(() => {
                            const tipoEt = !presente ? 'AUSENTE' : voto && secreta ? 'VOTOU' : voto || 'AGUARDANDO';
                            const et = ETIQUETA_VOTO[tipoEt] || ETIQUETA_VOTO.AGUARDANDO;
                            // card estreito: sem o icone e com a letra ajustada, para a palavra caber inteira
                            const vhPx = window.innerHeight / 100; const base = 1.6 * vhPx;
                            const disp = cardPx === null ? Infinity : cardPx - 3.8 * vhPx;
                            const largTexto = (f) => 0.8 * etiqueta.length * f;
                            const estreito = largTexto(base) + 3.1 * vhPx > disp;
                            const fonte = largTexto(base) <= disp ? '1.6vh' : `${Math.max(9, disp / (0.8 * etiqueta.length))}px`;
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.8vh', width: '100%', boxSizing: 'border-box', padding: '0.6vh 0.8vh', borderRadius: '1vh', background: et.fundo, border: `0.2vh solid ${et.borda}`, color: et.txt, fontSize: fonte, fontWeight: 900, letterSpacing: '0.05vh' }}>
                                {!estreito && <IconeVotoTV tipo={tipoEt} cor={et.ic} />}
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{etiqueta}</span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              </div>

              {/* Direita: o placar (o titulo da materia fica acima das fotos) */}
              <div style={{ flex: '0 0 52vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '1.6vh', background: 'rgba(255,255,255,0.03)', borderRadius: '1.5vh', border: '0.2vh solid rgba(255,255,255,0.07)', padding: '2vh 1.8vh', boxSizing: 'border-box', overflow: 'hidden' }}>
                <div style={{ fontSize: '1.7vh', color: 'rgba(255,255,255,0.55)', fontWeight: 800, letterSpacing: '0.2vh', textAlign: 'center' }}>PLACAR</div>

                {secretaAberta ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.6vh', background: 'rgba(59,130,246,0.12)', border: '0.3vh solid rgba(59,130,246,0.5)', borderRadius: '1.4vh', padding: '3vh 2vh', textAlign: 'center' }}>{/* PLENARIUM:SEGURANCA */}
                    <svg width="7vh" height="7vh" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    <div style={{ fontSize: '3.4vh', fontWeight: 900, color: '#dbeafe', letterSpacing: '0.2vh' }}>VOTAÇÃO SECRETA</div>
                    <div style={{ fontSize: '9vh', fontWeight: 900, color: '#fff', fontFamily: 'monospace', lineHeight: 1 }}>{String(totalVotos).padStart(2, '0')}</div>
                    <div style={{ fontSize: '2.2vh', fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>voto(s) registrado(s)</div>
                    <div style={{ fontSize: '1.9vh', fontWeight: 600, color: '#93c5fd' }}>O placar aparece ao encerrar a votação</div>
                  </div>
                ) : [
                  { rotulo: 'SIM', valor: contagem.SIM, cor: '#4ade80', bg: 'rgba(34,197,94,0.14)', borda: 'rgba(34,197,94,0.5)' },
                  { rotulo: 'NÃO', valor: contagem.NAO, cor: '#f87171', bg: 'rgba(239,68,68,0.14)', borda: 'rgba(239,68,68,0.5)' },
                  { rotulo: 'ABSTENÇÃO', valor: contagem.ABSTENCAO, cor: '#fbbf24', bg: 'rgba(234,179,8,0.14)', borda: 'rgba(234,179,8,0.5)' }
                ].map(item => (
                  <div key={item.rotulo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5vh', background: item.bg, border: `0.3vh solid ${item.borda}`, borderRadius: '1.4vh', padding: '1.6vh 2.2vh', flexShrink: 0 }}>{/* PLENARIUM:TV_PLACAR */}
                    <span style={{ fontSize: '4.4vh', fontWeight: 900, color: item.cor, letterSpacing: '0.1vh', whiteSpace: 'nowrap', lineHeight: 1 }}>{item.rotulo}</span>
                    <span style={{ fontSize: '8vh', fontWeight: 900, color: item.cor, fontFamily: 'monospace', lineHeight: 1 }}>{String(item.valor).padStart(2,'0')}</span>
                  </div>
                ))}

                {pautaAtiva.status !== 'EM_VOTACAO' && (
                  <div style={{ textAlign: 'center', padding: '1.1vh', borderRadius: '1.2vh', background: pautaAtiva.status === 'APROVADA' ? 'rgba(34,197,94,0.18)' : pautaAtiva.status === 'RETIRADA' ? 'rgba(148,163,184,0.15)' : 'rgba(239,68,68,0.18)', border: '0.3vh solid ' + (pautaAtiva.status === 'APROVADA' ? '#22c55e' : pautaAtiva.status === 'RETIRADA' ? '#94a3b8' : '#ef4444') }}>
                    <div style={{ fontSize: '1.6vh', color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: '0.15vh', marginBottom: '0.4vh' }}>RESULTADO</div>
                    <div style={{ fontSize: '3.4vh', fontWeight: 900, lineHeight: 1.1, color: pautaAtiva.status === 'APROVADA' ? '#4ade80' : pautaAtiva.status === 'RETIRADA' ? '#cbd5e1' : '#f87171' }}>
                      {pautaAtiva.status === 'APROVADA' ? 'APROVADA' : pautaAtiva.status === 'RETIRADA' ? 'RETIRADA' : 'REPROVADA'}
                    </div>
                  </div>
                )}
                <div style={{ textAlign: 'center', fontSize: '2.6vh', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>
                  {totalVotos} de {totalPresentes} presente(s) já votaram
                </div>
              </div>
              </div>
            </div>
) : (
            <div style={{ flex: 1, padding: `${s(22)} ${s(28)} ${s(14)}`, display: 'flex', flexDirection: 'column', gap: s(14), overflow: 'hidden', minHeight: 0 }}>
              {/* >>> PLENARIUM:TV_ORDEM_DIA -- lista da Ordem do Dia com visual de painel oficial */}
              <style>{'.pln-od-lista{scrollbar-width:none}.pln-od-lista::-webkit-scrollbar{display:none}@keyframes plnOdPulso{0%,100%{opacity:1}50%{opacity:.35}}'}</style>
              {(() => {
                const pautas = pautasOD; // PLENARIUM:AJUSTES_2110 -- a ata da sessao anterior nao entra na Ordem do Dia
                const nAp = pautas.filter(p => p.status === 'APROVADA').length;
                const nRe = pautas.filter(p => p.status === 'REPROVADA').length;
                const nRt = pautas.filter(p => p.status === 'RETIRADA').length;
                const nPe = pautas.length - nAp - nRe - nRt;
                const chip = (n, rot, cor, fundo) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: s(8), padding: `${s(6)} ${s(14)}`, borderRadius: 999, background: fundo, border: `1px solid ${cor}55`, fontSize: s(17), fontWeight: 700, color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' }}>
                    <b style={{ color: cor, fontSize: s(20), fontWeight: 900 }}>{n}</b>{rot}
                  </span>
                );
                return (
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: s(20), paddingBottom: s(12), borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
                    <div>
                      <div style={{ fontSize: s(15), fontWeight: 800, letterSpacing: s(3), color: '#93c5fd' }}>PAUTA DA SESSÃO</div>
                      <h2 style={{ fontSize: s(40), fontWeight: 900, margin: `${s(2)} 0 0`, letterSpacing: s(0.5), textTransform: 'uppercase', lineHeight: 1.05 }}>Ordem do Dia</h2>
                    </div>
                    <div style={{ display: 'flex', gap: s(10), flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {chip(pautas.length, pautas.length === 1 ? 'matéria' : 'matérias', '#93c5fd', 'rgba(37,99,235,0.14)')}
                      {nAp > 0 && chip(nAp, nAp === 1 ? 'aprovada' : 'aprovadas', '#4ade80', 'rgba(34,197,94,0.12)')}
                      {nRe > 0 && chip(nRe, nRe === 1 ? 'reprovada' : 'reprovadas', '#f87171', 'rgba(239,68,68,0.12)')}
                      {nRt > 0 && chip(nRt, nRt === 1 ? 'retirada' : 'retiradas', '#cbd5e1', 'rgba(148,163,184,0.12)')}
                      {nPe > 0 && chip(nPe, nPe === 1 ? 'a votar' : 'a votar', '#fbbf24', 'rgba(234,179,8,0.12)')}
                    </div>
                  </div>
                );
              })()}
              <div className="pln-od-lista" style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: s(10) }}>
                {pautasOD.map((p, i) => { // PLENARIUM:AJUSTES_2110
                  const ST = {
                    APROVADA: { rot: 'APROVADA', cor: '#4ade80', borda: '#22c55e', fundo: 'linear-gradient(90deg, rgba(34,197,94,0.24) 0%, rgba(34,197,94,0.10) 100%)' }, // PLENARIUM:TV_ORDEM_DIA_COR
                    REPROVADA: { rot: 'REPROVADA', cor: '#fca5a5', borda: '#ef4444', fundo: 'linear-gradient(90deg, rgba(239,68,68,0.26) 0%, rgba(239,68,68,0.10) 100%)' },
                    RETIRADA: { rot: 'RETIRADA', cor: '#cbd5e1', borda: '#64748b', fundo: 'rgba(148,163,184,0.05)' },
                    EM_VOTACAO: { rot: 'EM VOTAÇÃO', cor: '#fde68a', borda: '#eab308', fundo: 'rgba(234,179,8,0.08)' },
                    PENDENTE: { rot: 'AGUARDANDO', cor: '#94a3b8', borda: 'rgba(255,255,255,0.14)', fundo: 'rgba(255,255,255,0.03)' }
                  };
                  const st = ST[p.status] || ST.PENDENTE;
                  const votada = p.status === 'APROVADA' || p.status === 'REPROVADA';
                  const sim = p.votos?.filter(v => v.opcao === 'SIM').length || 0;
                  const nao = p.votos?.filter(v => v.opcao === 'NAO').length || 0;
                  const abs = p.votos?.filter(v => v.opcao === 'ABSTENCAO').length || 0;
                  const tipo = TIPO_LABEL[p.tipo] || p.tipo;
                  const num0 = String(p.numero || '').trim();
                  const num = num0.replace(/^[A-Za-z\u00c0-\u00ff]+\.?\s+/, ''); // sem a sigla: o tipo ja vem escrito
                  const tit = String(p.titulo || '').trim();
                  // titulo igual ao numero (cadastro rapido): mostra "Projeto de Lei nº X"
                  const titulo = !tit || tit === num0 || tit === num ? `${tipo}${num ? ` nº ${num}` : ''}` : tit;
                  return (
                    <div key={p.id} style={{ position: 'relative', flexShrink: 0, background: st.fundo, border: `${s(2)} solid ${votada ? st.borda + '99' : 'rgba(255,255,255,0.08)'}`, borderRadius: s(14), padding: `${s(14)} ${s(22)} ${s(14)} ${s(26)}`, display: 'flex', alignItems: 'center', gap: s(20), opacity: p.status === 'RETIRADA' ? 0.6 : 1, overflow: 'hidden' }}>
                      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: s(9), background: st.borda }} />
                      <div style={{ width: s(54), height: s(54), borderRadius: s(12), background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: s(22), fontWeight: 900, color: '#fff', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: s(10), marginBottom: s(4), flexWrap: 'wrap' }}>
                          <span style={{ fontSize: s(15), fontWeight: 800, letterSpacing: s(1.5), color: '#93c5fd', textTransform: 'uppercase' }}>{tipo}{num ? ` · Nº ${num}` : ''}</span>
                          {p.votacaoSecreta && <span style={{ fontSize: s(13), fontWeight: 800, letterSpacing: s(1), color: '#c4b5fd', border: '1px solid rgba(167,139,250,0.45)', borderRadius: 999, padding: `${s(1)} ${s(10)}` }}>VOTAÇÃO SECRETA</span>}
                        </div>
                        <div style={{ fontSize: titulo.length > 110 ? s(24) : s(29), fontWeight: 800, lineHeight: 1.25, color: '#f8fafc', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{titulo}</div>
                      </div>
                      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: s(8), minWidth: s(200) }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: s(8), padding: `${s(7)} ${s(16)}`, borderRadius: 999, background: votada ? st.borda : `${st.borda}22`, border: `${s(2)} solid ${votada ? st.borda : st.borda + '88'}`, color: votada ? '#fff' : st.cor, boxShadow: votada ? `0 0 ${s(16)} ${st.borda}66` : 'none', fontSize: s(18), fontWeight: 900, letterSpacing: s(1.2), whiteSpace: 'nowrap' }}>
                          <span style={{ width: s(10), height: s(10), borderRadius: '50%', background: votada ? '#fff' : st.cor, animation: p.status === 'EM_VOTACAO' ? 'plnOdPulso 1.4s ease-in-out infinite' : 'none' }} />
                          {st.rot}
                        </span>
                        {votada && (
                          <div style={{ display: 'flex', gap: s(14), fontSize: s(16), fontWeight: 700, color: 'rgba(255,255,255,0.55)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            <span>SIM <b style={{ color: '#4ade80', fontSize: s(19) }}>{sim}</b></span>
                            <span>NÃO <b style={{ color: '#f87171', fontSize: s(19) }}>{nao}</b></span>
                            <span>ABST. <b style={{ color: '#fbbf24', fontSize: s(19) }}>{abs}</b></span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* <<< PLENARIUM:TV_ORDEM_DIA */}
            </div>          )}
        </div>

      </div>

      {/* PLENARIUM:TV_SEM_RODAPE -- a barra do rodape (AGUARDANDO VOTACAO / iSolutions) saiu */}
    </div>
  );
}